/**
 * Top-bar HUD displaying Qi and Stone resource counters, current tech stage,
 * and build mode indicator with building unlock squares.
 */

import { Container, Text, TextStyle, Graphics } from 'pixi.js';
import { GameClientWorldState, BUILDING_UNLOCK_STAGE } from './game_client_world_state';
import { TechStage, BuildingKind } from './client_message_protocol';
import { getDeviceProfile } from './device_profile';

const TECH_LABELS: Record<TechStage, string> = {
  [TechStage.Foundation]: '筑基',
  [TechStage.GoldenCore]: '金丹',
  [TechStage.NascentSoul]: '元婴',
};

const BUILD_LABELS: Record<BuildingKind, string> = {
  [BuildingKind.SpiritRoot]: '灵根',
  [BuildingKind.GatheringArray]: '采集阵',
  [BuildingKind.RefiningArray]: '精炼阵',
  [BuildingKind.AlchemyFurnace]: '丹炉',
  [BuildingKind.Forge]: '锻造台',
  [BuildingKind.PuppetWorkshop]: '傀儡坊',
  [BuildingKind.DefenseTower]: '防御塔',
  [BuildingKind.SwordTower]: '剑塔',
  [BuildingKind.SlowTrap]: '减速陷阱',
  [BuildingKind.PuppetBarracks]: '傀儡营',
  [BuildingKind.TransporterStation]: '搬运站',
  [BuildingKind.HerbFarm]: '药田',
};

const BUILD_COLORS: Record<BuildingKind, number> = {
  [BuildingKind.SpiritRoot]: 0x44ff44,
  [BuildingKind.GatheringArray]: 0x4499ff,
  [BuildingKind.RefiningArray]: 0xff8844,
  [BuildingKind.AlchemyFurnace]: 0xff4444,
  [BuildingKind.Forge]: 0xcccccc,
  [BuildingKind.PuppetWorkshop]: 0xaa44ff,
  [BuildingKind.DefenseTower]: 0xffffff,
  [BuildingKind.SwordTower]: 0xffcc00,
  [BuildingKind.SlowTrap]: 0x44dddd,
  [BuildingKind.PuppetBarracks]: 0xff6688,
  [BuildingKind.TransporterStation]: 0x44ccff,
  [BuildingKind.HerbFarm]: 0x66cc44,
};

/** All 11 building kinds, in display order */
const ALL_BUILDING_KINDS: BuildingKind[] = [
  BuildingKind.SpiritRoot,
  BuildingKind.GatheringArray,
  BuildingKind.HerbFarm,
  BuildingKind.RefiningArray,
  BuildingKind.AlchemyFurnace,
  BuildingKind.Forge,
  BuildingKind.SwordTower,
  BuildingKind.SlowTrap,
  BuildingKind.TransporterStation,
  BuildingKind.DefenseTower,
  BuildingKind.PuppetWorkshop,
  BuildingKind.PuppetBarracks,
];

/** Key label for each building index (0-11) */
const KEY_LABELS: string[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='];

const SQUARE_SIZE = 26;
const SQUARE_GAP = 4;
const BAR_HEIGHT = 48;
const LOCKED_COLOR = 0x333333;

interface BuildingSquare {
  bg: Graphics;
  label: Text;
  icon: Text; // lock icon for locked
  rect: { x: number; y: number; w: number; h: number };
}

export class UiResourceHudBar {
  private container: Container;
  private parentContainer: Container;
  private bg: Graphics;
  private qiText: Text;
  private stoneText: Text;
  private herbText: Text;
  private pillText: Text;
  private artifactText: Text;
  private puppetCoreText: Text;
  private techText: Text;
  private buildLabel: Text;
  buildMode = false;
  buildKind: BuildingKind = BuildingKind.SpiritRoot;
  screenWidth: number;

  // Build squares
  private squares: BuildingSquare[] = [];
  private squaresContainer: Container;
  private tooltip: Text;
  private worldState: GameClientWorldState | null = null;
  private hoveredIndex: number = -1;

  constructor(container: Container, screenWidth = 1200, isMobile: boolean = false) {
    this.container = new Container();
    this.parentContainer = container;
    this.screenWidth = screenWidth;

    const style = new TextStyle({
      fontFamily: 'monospace',
      fontSize: 14,
      fill: 0xdddddd,
    });

    const accentStyle = new TextStyle({
      fontFamily: 'monospace',
      fontSize: 14,
      fill: 0x44ff44,
    });

    this.bg = new Graphics();
    this.qiText = new Text({ text: 'Qi: 0', style });
    this.stoneText = new Text({ text: '石: 0', style });
    this.herbText = new Text({ text: '药: 0', style });
    this.pillText = new Text({ text: '丹: 0', style });
    this.artifactText = new Text({ text: '器: 0', style });
    this.puppetCoreText = new Text({ text: '傀: 0', style: new TextStyle({ fontFamily: 'monospace', fontSize: 14, fill: 0xff8800 }) });
    this.techText = new Text({ text: '道: 筑基', style });
    this.buildLabel = new Text({ text: '', style: accentStyle });

    this.qiText.x = 12;
    this.qiText.y = 8;
    this.stoneText.x = 12;
    this.stoneText.y = 26;
    this.herbText.x = 120;
    this.herbText.y = 8;
    this.pillText.x = 120;
    this.pillText.y = 26;
    this.artifactText.x = 220;
    this.artifactText.y = 26;
    this.puppetCoreText.x = 300;
    this.puppetCoreText.y = 8;
    this.techText.x = 220;
    this.techText.y = 8;
    this.buildLabel.x = 400;
    this.buildLabel.y = 8;

    this.container.addChild(this.bg, this.qiText, this.stoneText, this.herbText, this.pillText, this.artifactText, this.puppetCoreText, this.techText, this.buildLabel);

    // Squares container (shown in build mode)
    this.squaresContainer = new Container();
    this.squaresContainer.visible = false;
    this.container.addChild(this.squaresContainer);

    // Tooltip text
    this.tooltip = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'monospace',
        fontSize: 12,
        fill: 0xffffff,
        dropShadow: { color: 0x000000, alpha: 0.8, blur: 2, distance: 1 },
      }),
    });
    this.tooltip.visible = false;
    this.container.addChild(this.tooltip);

    // Create all 11 building squares
    this.createSquares();

    // Mousemove on the container for tooltip
    this.container.eventMode = 'static';
    this.container.on('mousemove', (e) => this.onMouseMove(e));
    this.container.on('mouseleave', () => { this.hoveredIndex = -1; this.tooltip.visible = false; });

    this.parentContainer.addChild(this.container);
    this.drawBg();
    if (isMobile) this.setCompactMode(true);
  }

  private createSquares(): void {
    for (let i = 0; i < ALL_BUILDING_KINDS.length; i++) {
      const x = 400 + i * (SQUARE_SIZE + SQUARE_GAP);
      const y = BAR_HEIGHT + 4;

      const bg = new Graphics();
      const label = new Text({
        text: KEY_LABELS[i],
        style: new TextStyle({ fontFamily: 'monospace', fontSize: 11, fill: 0xffffff }),
      });
      label.anchor.set(0.5);
      const icon = new Text({
        text: '🔒',
        style: new TextStyle({ fontSize: 10 }),
      });
      icon.anchor.set(0.5);

      bg.x = x;
      bg.y = y;
      label.x = x + SQUARE_SIZE / 2;
      label.y = y + SQUARE_SIZE / 2;
      icon.x = x + SQUARE_SIZE / 2;
      icon.y = y + SQUARE_SIZE - 8;

      this.squaresContainer.addChild(bg, label, icon);
      this.squares.push({ bg, label, icon, rect: { x, y, w: SQUARE_SIZE, h: SQUARE_SIZE } });
    }
  }

  private onMouseMove(e: any): void {
    const localX = e.getLocalPosition(this.container).x;
    const localY = e.getLocalPosition(this.container).y;

    this.hoveredIndex = -1;
    for (let i = 0; i < this.squares.length; i++) {
      const r = this.squares[i].rect;
      if (localX >= r.x && localX <= r.x + r.w && localY >= r.y && localY <= r.y + r.h) {
        this.hoveredIndex = i;
        break;
      }
    }

    if (this.hoveredIndex >= 0 && this.buildMode) {
      const kind = ALL_BUILDING_KINDS[this.hoveredIndex];
      const name = BUILD_LABELS[kind] ?? kind;
      if (this.worldState && !this.worldState.isUnlocked(kind)) {
        const stage = BUILDING_UNLOCK_STAGE[kind];
        const stageLabel = TECH_LABELS[stage] ?? stage;
        this.tooltip.text = `${name}\n需要: ${stageLabel}`;
      } else {
        this.tooltip.text = name;
      }
      this.tooltip.x = Math.min(localX + 12, this.screenWidth - 160);
      this.tooltip.y = localY - 32;
      this.tooltip.visible = true;
    } else {
      this.tooltip.visible = false;
    }
  }

  private drawBg(): void {
    this.bg.clear();
    const h = this.buildMode ? BAR_HEIGHT + SQUARE_SIZE + 10 : BAR_HEIGHT;
    this.bg.rect(0, 0, this.screenWidth, h);
    this.bg.fill({ color: 0x000000, alpha: 0.6 });
    this.bg.rect(0, h - 1, this.screenWidth, 1);
    this.bg.fill(0x444444);
  }

  setScreenWidth(w: number): void {
    this.screenWidth = w;
    this.drawBg();
  }

  update(worldState: GameClientWorldState): void {
    this.worldState = worldState;
    this.qiText.text = `Qi: ${Math.floor(worldState.spiritQi)}`;
    this.stoneText.text = `石: ${Math.floor(worldState.spiritStone)}`;
    this.herbText.text = `药: ${Math.floor(worldState.herb)}`;
    this.pillText.text = `丹: ${Math.floor(worldState.pill)}`;
    this.artifactText.text = `器: ${Math.floor(worldState.artifact)}`;
    this.puppetCoreText.text = `傀: ${Math.floor(worldState.puppetCores)}`;
    this.techText.text = `道: ${TECH_LABELS[worldState.techStage] ?? '筑基'}`;

    if (this.buildMode) {
      const label = BUILD_LABELS[this.buildKind] ?? '灵根';
      this.buildLabel.text = `[B] 建造: ${label}  [1-9,0,-切换]  (Esc取消)`;
      this.squaresContainer.visible = true;
      this.refreshSquares(worldState);
    } else {
      this.buildLabel.text = '按 B 进入建造模式';
      this.squaresContainer.visible = false;
      this.tooltip.visible = false;
    }

    this.drawBg();
  }

  setCompactMode(compact: boolean): void {
    if (compact) {
      // Responsive single-row HUD using device profile
      const profile = getDeviceProfile();
      const fs = profile.hudFontSize;
      const rowY = 4;
      const gap = Math.max(48, this.screenWidth / 8);

      this.qiText.style.fontSize = fs;
      this.stoneText.style.fontSize = fs;
      this.herbText.style.fontSize = fs;
      this.pillText.style.fontSize = fs;
      this.artifactText.style.fontSize = fs;
      this.puppetCoreText.style.fontSize = fs;
      this.techText.style.fontSize = fs;

      let x = 6;
      this.qiText.x = x; this.qiText.y = rowY;
      x += gap; this.stoneText.x = x; this.stoneText.y = rowY;
      x += gap; this.herbText.x = x; this.herbText.y = rowY;
      x += gap; this.pillText.x = x; this.pillText.y = rowY;
      x += gap; this.artifactText.x = x; this.artifactText.y = rowY;
      x += gap; this.puppetCoreText.x = x; this.puppetCoreText.y = rowY;
      x += gap; this.techText.x = x; this.techText.y = rowY;

      this.buildLabel.visible = false;
      this.squaresContainer.visible = false;
      this.bg.clear();
      this.bg.roundRect(0, 0, x + 24, fs * 2 + 6, 4);
      this.bg.fill({ color: 0x0a0a1e, alpha: 0.85 });
    }
  }

  private refreshSquares(worldState: GameClientWorldState): void {
    for (let i = 0; i < ALL_BUILDING_KINDS.length; i++) {
      const kind = ALL_BUILDING_KINDS[i];
      const square = this.squares[i];
      const unlocked = worldState.isUnlocked(kind);
      const isSelected = kind === this.buildKind;

      square.bg.clear();
      if (unlocked) {
        const color = BUILD_COLORS[kind] ?? 0x888888;
        square.bg.rect(0, 0, SQUARE_SIZE, SQUARE_SIZE);
        square.bg.fill({ color, alpha: 1 });
        if (isSelected) {
          square.bg.rect(0, 0, SQUARE_SIZE, SQUARE_SIZE);
          square.bg.stroke({ color: 0xffffff, width: 2 });
        }
        square.label.visible = true;
        square.icon.visible = false;
        square.label.style.fill = 0x000000;
      } else {
        square.bg.rect(0, 0, SQUARE_SIZE, SQUARE_SIZE);
        square.bg.fill({ color: LOCKED_COLOR, alpha: 1 });
        square.bg.rect(0.5, 0.5, SQUARE_SIZE - 0.5, SQUARE_SIZE - 0.5);
        square.bg.stroke({ color: 0x555555, width: 0.5 });
        square.label.visible = true;
        square.label.style.fill = 0x888888;
        square.icon.visible = true;
      }
    }
  }
}
